import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add, Edit, Delete, Circle, LocationOn } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { 
  addDosingPumpToSite, 
  getSiteWithInstruments, 
  updateDosingPumpInSite, 
  deleteDosingPumpFromSite,
  getTagNamesByProjectId,
  getTelemetryData,
  logActivity
} from '../../api/apiService';
import { TELEMETRY_LIVE_POLL_MS } from '../../constants/telemetry';

const DosingPumpsTab = ({ projectId, siteData }) => {
  const userRole = useSelector((state) => state.auth.user?.role);
  const currentUser = useSelector((state) => state.auth.user);
  const isAdminOrManager = userRole === 'Admin' || userRole === 'Manager';

  // State for dosing pumps data
  const [dosingPumps, setDosingPumps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [telemetryData, setTelemetryData] = useState({});
  const [isLoadingTelemetry, setIsLoadingTelemetry] = useState(false);

  // State for tag names
  const [tagNames, setTagNames] = useState([]);
  const [loadingTagNames, setLoadingTagNames] = useState(false);

  // State for dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedDosingPump, setSelectedDosingPump] = useState(null);
  const [formData, setFormData] = useState({
    pumpDescription: '',
    pumpLocation: '',
    // chemicalType: '',
    onOffTagName: '',
    tripTagName: ''
  });

  // Fetch dosing pumps data when component mounts or projectId changes
  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;

      try {
        setLoading(true);
        const fullSiteData = await getSiteWithInstruments(projectId);
        setDosingPumps(fullSiteData.dosingPumps || []);
      } catch (error) {
        console.error('Error fetching site data:', error);
        setDosingPumps([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  // Fetch tag names from omsTelemetryLive table
  useEffect(() => {
    const fetchTagNames = async () => {
      if (!projectId) return;
      
      try {
        setLoadingTagNames(true);
        const names = await getTagNamesByProjectId(projectId);
        setTagNames(names);
      } catch (error) {
        console.error('Error fetching tag names:', error);
        setTagNames([]);
      } finally {
        setLoadingTagNames(false);
      }
    };

    fetchTagNames();
  }, [projectId]);

  // Fetch telemetry data for dosing pumps
  useEffect(() => {
    const fetchTelemetryData = async () => {
      if (!projectId || !dosingPumps.length) return;

      try {
        setIsLoadingTelemetry(true);
        const tagIds = [];
        
        // Collect all tag IDs from dosing pumps
        dosingPumps.forEach(pump => {
          if (pump.onOffTagName) {
            // Format tagId from tagName: PRJ1255_PLC1:DO_301
            const serverName = siteData?.serverName || 'PLC1';
            const cleanServerName = serverName.replace(`${projectId}_`, '');
            const formattedTagId = `${projectId}_${cleanServerName}:${pump.onOffTagName}`;
            tagIds.push(formattedTagId);
          }
          if (pump.tripTagName) {
            // Format tagId from tagName: PRJ1255_PLC1:DI_401
            const serverName = siteData?.serverName || 'PLC1';
            const cleanServerName = serverName.replace(`${projectId}_`, '');
            const formattedTagId = `${projectId}_${cleanServerName}:${pump.tripTagName}`;
            tagIds.push(formattedTagId);
          }
        });

        if (tagIds.length === 0) return;

        console.log('[DosingPumpsTab] Fetching telemetry for tagIds:', tagIds);
        const data = await getTelemetryData(projectId, tagIds);
        
        // Create a map of tagId to value
        const telemetryMap = {};
        data.forEach(item => {
          telemetryMap[item.tagId] = {
            value: item.value,
            timestamp: item.timestamp,
            localTime: item.localTime,
            deviceLocalTime: item.deviceLocalTime
          };
        });
        
        setTelemetryData(telemetryMap);
      } catch (error) {
        console.error('[DosingPumpsTab] Error fetching telemetry data:', error);
      } finally {
        setIsLoadingTelemetry(false);
      }
    };

    // Initial fetch
    fetchTelemetryData();

    const intervalId = setInterval(fetchTelemetryData, TELEMETRY_LIVE_POLL_MS);

    return () => clearInterval(intervalId);
  }, [projectId, dosingPumps, siteData]);

  // Handle form data change
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    const newDosingPump = {
      id: Date.now(),
      pumpDescription: formData.pumpDescription,
      pumpLocation: formData.pumpLocation,
      // chemicalType: formData.chemicalType,
      onOffTagName: formData.onOffTagName,
      tripTagName: formData.tripTagName,
    };

    try {
      await addDosingPumpToSite(projectId, newDosingPump);
      console.log('New dosing pump added successfully!');
      
      // Log add dosing pump activity to DynamoDB
      try {
        await logActivity(currentUser.email, `Added dosing pump: ${newDosingPump.pumpDescription}`);
      } catch (err) {
        console.error('Error logging activity to DynamoDB:', err);
      }
      
      const fullSiteData = await getSiteWithInstruments(projectId);
      setDosingPumps(fullSiteData.dosingPumps || []);
      setOpenDialog(false);
      setFormData({
        pumpDescription: '',
        pumpLocation: '',
        // chemicalType: '',
        onOffTagName: '',
        tripTagName: ''
      });
    } catch (error) {
      console.error('Error adding dosing pump to DynamoDB:', error);
    }
  };

  // Handle Edit
  const handleEdit = (dosingPump) => {
    setSelectedDosingPump(dosingPump);
    setFormData({
      pumpDescription: dosingPump.pumpDescription || '',
      pumpLocation: dosingPump.pumpLocation || '',
      // chemicalType: dosingPump.chemicalType || '',
      onOffTagName: dosingPump.onOffTagName || '',
      tripTagName: dosingPump.tripTagName || '',
    });
    setOpenEditDialog(true);
  };

  // Handle Delete
  const handleDelete = (dosingPump) => {
    setSelectedDosingPump(dosingPump);
    setOpenDeleteDialog(true);
  };

  // Handle Edit Submit
  const handleEditSubmit = async () => {
    try {
      const updatedDosingPump = {
        ...selectedDosingPump,
        ...formData,
      };
      
      // Ensure we're not creating a new ID
      if (!updatedDosingPump.id) {
        updatedDosingPump.id = selectedDosingPump.id;
      }

      if (userRole === 'Admin' || userRole === 'Manager') {
        await updateDosingPumpInSite(projectId, updatedDosingPump);
        // Log edit dosing pump activity
        try {
          await logActivity(currentUser.email, `Edited dosing pump: ${updatedDosingPump.pumpDescription}`);
        } catch (err) {
          console.error('Error logging edit dosing pump activity to DynamoDB:', err);
        }
      }
      // Update local state for all roles
      const fullSiteData = await getSiteWithInstruments(projectId);
      setDosingPumps(fullSiteData.dosingPumps || []);
      
      // Close dialog and reset state
      setOpenEditDialog(false);
      setSelectedDosingPump(null);
      setFormData({
        pumpDescription: '',
        pumpLocation: '',
        // chemicalType: '',
        onOffTagName: '',
        tripTagName: ''
      });
    } catch (error) {
      console.error('Error updating dosing pump:', error);
    }
  };

  // Handle Delete Confirm
  const handleDeleteConfirm = async () => {
    try {
      await deleteDosingPumpFromSite(projectId, selectedDosingPump.id);
      // Log delete dosing pump activity
      try {
        await logActivity(currentUser.email, `Deleted dosing pump: ${selectedDosingPump.pumpDescription}`);
      } catch (err) {
        console.error('Error logging delete dosing pump activity to DynamoDB:', err);
      }
      console.log('Dosing pump deleted successfully!');
      const fullSiteData = await getSiteWithInstruments(projectId);
      setDosingPumps(fullSiteData.dosingPumps || []);
      setOpenDeleteDialog(false);
      setSelectedDosingPump(null);
    } catch (error) {
      console.error('Error deleting dosing pump:', error);
    }
  };

  // Get dosing pump status based on telemetry data
  const getDosingPumpStatus = (pump) => {
    if (!pump.onOffTagName) {
      return 'Unknown';
    }
    
    // Format tagId from tagName for telemetry lookup
    const serverName = siteData?.serverName || 'PLC1';
    const cleanServerName = serverName.replace(`${projectId}_`, '');
    const onOffTagId = `${projectId}_${cleanServerName}:${pump.onOffTagName}`;
    
    if (!telemetryData[onOffTagId]) {
      return 'Unknown';
    }
    
    const onOffValue = telemetryData[onOffTagId].value;
    
    // Check for trip condition first
    if (pump.tripTagName) {
      const tripTagId = `${projectId}_${cleanServerName}:${pump.tripTagName}`;
      if (telemetryData[tripTagId]) {
        const tripValue = telemetryData[tripTagId].value;
        if (tripValue === '1' || tripValue === 1 || tripValue === 'true' || tripValue === true) {
          return 'Trip';
        }
      }
    }
    
    // Check on/off status
    if (onOffValue === '1' || onOffValue === 1 || onOffValue === 'true' || onOffValue === true) {
      return 'On';
    } else if (onOffValue === '0' || onOffValue === 0 || onOffValue === 'false' || onOffValue === false) {
      return 'Off';
    }
    
    return 'Unknown';
  };

  // Get status indicator component
  const getStatusIndicator = (status) => {
    const isOn = status === 'On';
    const isOff = status === 'Off';
    const isTrip = status === 'Trip';
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* On Indicator */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1 
        }}>
          <Circle sx={(theme) => ({ 
            color: isOn ? theme.palette.status.normalGreen : theme.palette.grey[300], 
            fontSize: 24,
            transition: 'color 0.3s ease'
          })} />
          <Typography variant="caption" sx={(theme) => ({ 
            color: isOn ? theme.palette.status.normalGreen : theme.palette.grey[500], 
            fontWeight: isOn ? 600 : 400,
            fontSize: '0.75rem',
            transition: 'all 0.3s ease'
          })}>
            ON
          </Typography>
        </Box>
        
        {/* Off Indicator */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1 
        }}>
          <Circle sx={(theme) => ({ 
            color: isOff ? theme.palette.status.alarmRed : theme.palette.grey[300], 
            fontSize: 24,
            transition: 'color 0.3s ease'
          })} />
          <Typography variant="caption" sx={(theme) => ({ 
            color: isOff ? theme.palette.status.alarmRed : theme.palette.grey[500], 
            fontWeight: isOff ? 600 : 400,
            fontSize: '0.75rem',
            transition: 'all 0.3s ease'
          })}>
            OFF
          </Typography>
        </Box>
        
        {/* Trip Indicator */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1 
        }}>
          <Circle sx={(theme) => ({ 
            color: isTrip ? theme.palette.status.warningAmber : theme.palette.grey[300], 
            fontSize: 24,
            transition: 'color 0.3s ease'
          })} />
          <Typography variant="caption" sx={(theme) => ({ 
            color: isTrip ? theme.palette.status.warningAmber : theme.palette.grey[500], 
            fontWeight: isTrip ? 600 : 400,
            fontSize: '0.75rem',
            transition: 'all 0.3s ease'
          })}>
            TRIP
          </Typography>
        </Box>
      </Box>
    );
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress sx={{ color: (theme) => theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: { xs: '300px', sm: '350px', md: '400px' },
      overflow: 'auto',
      position: 'relative'
    }}>
      <Stack spacing={2}>
        {dosingPumps.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px',
            color: (theme) => theme.palette.text.secondary
          }}>
            <Typography variant="body1">No dosing pumps found. Add your first dosing pump using the + button.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {dosingPumps.map((pump, index) => {
              const status = getDosingPumpStatus(pump);
              
              return (
                <Card key={pump.id || index} sx={{ 
                  borderRadius: '16px', 
                  boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 4px 12px rgba(0,0,0,0.08)',
                  background: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 10px 28px rgba(0,0,0,0.45)' : '0 8px 25px rgba(0,0,0,0.15)',
                    border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(25, 118, 210, 0.2)',
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 700, 
                        fontSize: '1.1rem',
                        color: (theme) => theme.palette.text.primary,
                        lineHeight: 1.3
                      }}>
                        {pump.pumpDescription}
                      </Typography>
                      {isAdminOrManager && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit Dosing Pump">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(pump);
                              }}
                              sx={{
                                color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) },
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Dosing Pump">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(pump);
                              }}
                              sx={{
                                color: (theme) => theme.palette.error.main,
                                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                    <Stack spacing={2}>
                      {/* <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        p: 1.5,
                        backgroundColor: 'rgba(25, 118, 210, 0.05)',
                        borderRadius: '8px'
                      }}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600, 
                          color: '#1976D2',
                          fontSize: '0.875rem'
                        }}>
                          {pump.chemicalType}
                        </Typography>
                      </Box> */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        p: 1.5,
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                        borderRadius: '8px'
                      }}>
                        <Typography variant="body2" sx={{ 
                          color: (theme) => theme.palette.text.secondary,
                          fontSize: '0.875rem',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <LocationOn sx={{ fontSize: 16, mr: 0.5, color: (theme) => theme.palette.text.secondary }} /> {pump.pumpLocation}
                        </Typography>
                      </Box>
                      
                      {/* Status Indicators */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        p: 2,
                        backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                        borderRadius: '8px'
                      }}>
                        {isLoadingTelemetry ? (
                          <CircularProgress size={20} sx={{ color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main }} />
                        ) : (
                          getStatusIndicator(status)
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Stack>
      
      {isAdminOrManager && (
        <Fab
          color="primary"
          aria-label="add dosing pump"
          onClick={() => setOpenDialog(true)}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
            backgroundColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
            color: (theme) => theme.palette.getContrastText(theme.palette.brand?.royalBlue || theme.palette.primary.main),
            '&:hover': {
              backgroundColor: (theme) => theme.palette.mode === 'dark' 
                ? alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, 0.85)
                : theme.palette.brand?.royalBlue || theme.palette.primary.main,
              filter: (theme) => theme.palette.mode === 'dark' ? 'none' : 'brightness(0.95)',
            },
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Add Dosing Pump Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: "12px", 
            padding: {
              xs: "12px",
              sm: "16px",
              md: "20px"
            },
            boxShadow: "0px 8px 24px rgba(0,0,0,0.15)"
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: "600", 
          color: "#1A1F36",
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: {
            xs: '1.1rem',
            sm: '1.25rem',
            md: '1.5rem'
          }
        }}>
          Add New Dosing Pump
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: "16px" }}>
            <TextField
              fullWidth
              label="Pump Description"
              name="pumpDescription"
              value={formData.pumpDescription}
              onChange={handleFormChange}
              variant="outlined"
              required
            />
            <TextField
              fullWidth
              label="Pump Location"
              name="pumpLocation"
              value={formData.pumpLocation}
              onChange={handleFormChange}
              variant="outlined"
              required
            />
            {/* <FormControl fullWidth>
              <InputLabel id="chemical-type-label">Chemical Type</InputLabel>
              <Select
                labelId="chemical-type-label"
                label="Chemical Type"
                name="chemicalType"
                value={formData.chemicalType}
                onChange={handleFormChange}
                variant="outlined"
                required
              >
                <MenuItem value="">Select Chemical Type</MenuItem>
                <MenuItem value="Chlorine">Chlorine</MenuItem>
                <MenuItem value="pH">pH</MenuItem>
                <MenuItem value="Coagulant">Coagulant</MenuItem>
                <MenuItem value="Polymer">Polymer</MenuItem>
                <MenuItem value="Acid">Acid</MenuItem>
                <MenuItem value="Alkali">Alkali</MenuItem>
                <MenuItem value="Flocculant">Flocculant</MenuItem>
                <MenuItem value="Disinfectant">Disinfectant</MenuItem>
              </Select>
            </FormControl> */}
            <FormControl fullWidth>
              <InputLabel id="on-off-tag-label">On/Off Tag Name</InputLabel>
              <Select
                labelId="on-off-tag-label"
                label="On/Off Tag Name"
                name="onOffTagName"
                value={formData.onOffTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
                required
              >
                <MenuItem value="">Select an On/Off Tag Name</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="trip-tag-label">Trip Tag Name</InputLabel>
              <Select
                labelId="trip-tag-label"
                label="Trip Tag Name"
                name="tripTagName"
                value={formData.tripTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
              >
                <MenuItem value="">Select a Trip Tag Name (Optional)</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ padding: "16px" }}>
          <Button 
            onClick={() => setOpenDialog(false)} 
            color="secondary" 
            sx={{ 
              textTransform: "none",
              color: '#666',
              '&:hover': {
                backgroundColor: '#F5F5F5',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary" 
            sx={{ 
              textTransform: "none", 
              boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
              backgroundColor: '#1976D2',
              '&:hover': {
                backgroundColor: '#1565C0',
              }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dosing Pump Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: '600', color: '#1A1F36' }}>Edit Dosing Pump</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: '16px' }}>
            <TextField
              fullWidth
              label="Pump Description"
              name="pumpDescription"
              value={formData.pumpDescription}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Pump Location"
              name="pumpLocation"
              value={formData.pumpLocation}
              onChange={handleFormChange}
              variant="outlined"
            />
            {/* <FormControl fullWidth>
              <InputLabel id="edit-chemical-type-label">Chemical Type</InputLabel>
              <Select
                labelId="edit-chemical-type-label"
                label="Chemical Type"
                name="chemicalType"
                value={formData.chemicalType}
                onChange={handleFormChange}
                variant="outlined"
              >
                <MenuItem value="">Select Chemical Type</MenuItem>
                <MenuItem value="Chlorine">Chlorine</MenuItem>
                <MenuItem value="pH">pH</MenuItem>
                <MenuItem value="Coagulant">Coagulant</MenuItem>
                <MenuItem value="Polymer">Polymer</MenuItem>
                <MenuItem value="Acid">Acid</MenuItem>
                <MenuItem value="Alkali">Alkali</MenuItem>
                <MenuItem value="Flocculant">Flocculant</MenuItem>
                <MenuItem value="Disinfectant">Disinfectant</MenuItem>
              </Select>
            </FormControl> */}
            <FormControl fullWidth>
              <InputLabel id="edit-on-off-tag-label">On/Off Tag Name</InputLabel>
              <Select
                labelId="edit-on-off-tag-label"
                label="On/Off Tag Name"
                name="onOffTagName"
                value={formData.onOffTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
              >
                <MenuItem value="">Select an On/Off Tag Name</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="edit-trip-tag-label">Trip Tag Name</InputLabel>
              <Select
                labelId="edit-trip-tag-label"
                label="Trip Tag Name"
                name="tripTagName"
                value={formData.tripTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
              >
                <MenuItem value="">Select a Trip Tag Name (Optional)</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained" 
            color="primary"
          >
            Submit
          </Button>
          <Button 
            onClick={() => setOpenEditDialog(false)} 
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dosing Pump Dialog */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: '600', color: '#1A1F36' }}>Delete Dosing Pump</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this dosing pump?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="primary"
          >
            Delete
          </Button>
          <Button 
            onClick={() => setOpenDeleteDialog(false)} 
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DosingPumpsTab; 